import type { RecordView } from '@kit/sync/types/record-view';
import { shoppingTotals } from '@/entities/shopping-item/helpers/shopping-totals';
import type { ShoppingItem } from '@/entities/shopping-item/schemas/shopping-item';
import { StatCard } from '@/shared/components/stat-card';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatCents } from '@/shared/lib/money/format-cents';

type ShoppingStatsProps = {
  readonly list: readonly RecordView<ShoppingItem>[];
};

/** A zero that only means "nothing has a price" is shown as a dash. */
function money(cents: number): string {
  return cents > 0 ? formatCents(cents) : '—';
}

/** Phones: one line under the tabs. Desktop: four cards. Both follow the filters. */
export function ShoppingStats({ list }: ShoppingStatsProps) {
  const { t, tn } = useTranslation();
  const totals = shoppingTotals(list);
  const count = totals.pendingCount;
  const unestimated =
    totals.unestimated > 0
      ? tn(totals.unestimated, '{count} without an estimate', '{count} without an estimate')
      : null;
  return (
    <>
      <p className="text-sm text-muted-foreground md:hidden">
        {[
          tn(count, '{count} to buy', '{count} to buy'),
          totals.estimated > 0 ? `≈ ${formatCents(totals.estimated)}` : null,
          unestimated,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      <div className="grid grid-cols-4 gap-3 max-md:hidden">
        <StatCard
          highlight
          label={t('Pending estimate')}
          value={money(totals.estimated)}
          hint={unestimated ?? tn(count, '{count} item', '{count} items')}
        />
        <StatCard label={t('High priority')} value={money(totals.byPriority.high)} />
        <StatCard
          label={t('Medium and low')}
          value={money(totals.byPriority.medium + totals.byPriority.low)}
        />
        <StatCard
          label={t('Already spent')}
          value={money(totals.spent)}
          hint={t('On what was bought from the list')}
        />
      </div>
    </>
  );
}
